import React, { useState, useCallback, useMemo } from "react";
import { ScrollView, View } from "react-native";
import { useRouter } from "expo-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useI18n } from "@/context/I18nContext";
import { AppText } from "@/components/ui/AppText";
import { PageFooter } from "@/components/ui/PageFooter";
import { usePagination } from "@/hooks/usePagination";
import {
  localizationApi,
  type AddLocalizationKeyPayload,
  type ContentType,
  type LocalizationKey,
  type LocalizationListResponse,
  type SortField,
  type StatusFilter,
  type ContentCountStats,
} from "@/lib/api/localization";
import { LocalizationStatCards } from "./components/LocalizationStatCards";
import { LocalizationList } from "./components/LocalizationList";
import { AddContentModal } from "./components/AddContentModal";

const PAGE_SIZE = 10;

/**
 * Main screen for the Localization tab in the staff dashboard.
 *
 * - "other" tab: server-side paginated via GET /localization/entries
 * - "categories" / "industries" tabs: full list fetched, client-side filter/sort/paginate
 * - Stat cards: GET /localization/statistics + categories/industries list counts
 *
 * Route: (staff)/(dashboard)/localization
 * Matches Figma "Staff / Localization / List" composition.
 */
export function LocalizationTabScreen() {
  const { t } = useI18n();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { top } = useSafeAreaInsets();

  const [contentType, setContentType] = useState<ContentType>("categories");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [sortField, setSortField] = useState<SortField>("alpha_asc");
  const [addModalVisible, setAddModalVisible] = useState(false);

  const { page, setPage, resetPage, search, setSearch, debouncedSearch } = usePagination({
    initialPage: 1,
    initialLimit: PAGE_SIZE,
  });

  // ── Data fetching ──────────────────────────────────────────────────────────

  const sharedQueryParams = {
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch,
    status: statusFilter,
    sort: sortField,
  };

  // Always-enabled lightweight count queries for stat cards (unfiltered totals).
  const { data: categoryStats } = useQuery<ContentCountStats>({
    queryKey: ["localization", "categories", "stats"],
    queryFn: () => localizationApi.getCategoryStats(),
  });

  const { data: industryStats } = useQuery<ContentCountStats>({
    queryKey: ["localization", "industries", "stats"],
    queryFn: () => localizationApi.getIndustryStats(),
  });

  const { data: categoriesPaginated, isLoading: loadingCategories } = useQuery({
    queryKey: ["localization", "categories", "list", sharedQueryParams],
    queryFn: () => localizationApi.listCategoriesPaginated(sharedQueryParams),
    enabled: contentType === "categories",
    placeholderData: (prev) => prev,
  });

  const { data: industriesPaginated, isLoading: loadingIndustries } = useQuery({
    queryKey: ["localization", "industries", "list", sharedQueryParams],
    queryFn: () => localizationApi.listIndustriesPaginated(sharedQueryParams),
    enabled: contentType === "industries",
    placeholderData: (prev) => prev,
  });

  // Global unfiltered counts for stat cards (covers all "other" i18n keys)
  const { data: otherStats } = useQuery({
    queryKey: ["localization", "statistics"],
    queryFn: () => localizationApi.getStatistics(),
  });

  // Server-side paginated query — only active when on the "other" tab
  const { data: otherPaginated, isLoading: loadingOther } = useQuery({
    queryKey: [
      "localization",
      "other",
      "list",
      page,
      PAGE_SIZE,
      debouncedSearch,
      statusFilter,
      sortField,
    ],
    queryFn: () =>
      localizationApi.listOtherKeysPaginated({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch,
        status: statusFilter,
        sort: sortField,
      }),
    enabled: contentType === "other",
    placeholderData: (prev) => prev,
  });

  const isLoading =
    contentType === "other"
      ? loadingOther
      : contentType === "categories"
        ? loadingCategories
        : loadingIndustries;

  // ── Derive paginated view ──────────────────────────────────────────────────

  const listData = useMemo((): LocalizationListResponse => {
    const activePaginated =
      contentType === "categories"
        ? categoriesPaginated
        : contentType === "industries"
          ? industriesPaginated
          : otherPaginated;

    // Stat cards always use unfiltered global counts from the dedicated stat queries.
    const completedCount =
      (categoryStats?.completedCount ?? 0) +
      (industryStats?.completedCount ?? 0) +
      (otherStats?.totalCompleted ?? 0);
    const allCount =
      (categoryStats?.total ?? 0) + (industryStats?.total ?? 0) + (otherStats?.totalEntries ?? 0);
    const pendingCount = allCount - completedCount;

    return {
      items: activePaginated?.items ?? [],
      total: activePaginated?.total ?? 0,
      completedCount,
      pendingCount,
      allCount,
      categoriesCount: categoryStats?.total ?? 0,
      industriesCount: industryStats?.total ?? 0,
      otherCount: otherStats?.totalEntries ?? 0,
      page: activePaginated?.page ?? page,
      pageSize: PAGE_SIZE,
    };
  }, [
    categoryStats,
    industryStats,
    categoriesPaginated,
    industriesPaginated,
    otherStats,
    otherPaginated,
    contentType,
    page,
  ]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleContentTypeChange = useCallback(
    (type: ContentType) => {
      setContentType(type);
      resetPage();
    },
    [resetPage]
  );

  const handleStatusFilterChange = useCallback(
    (filter: StatusFilter) => {
      setStatusFilter(filter);
      resetPage();
    },
    [resetPage]
  );

  const handleSortChange = useCallback(
    (sort: SortField) => {
      setSortField(sort);
      resetPage();
    },
    [resetPage]
  );

  const handleCardPress = useCallback(
    (item: LocalizationKey) => {
      const params = new URLSearchParams({ contentType: item.contentType });
      if (item.id) params.set("id", item.id);
      router.push(`/(staff)/localization/${encodeURIComponent(item.key)}?${params}` as any);
    },
    [router]
  );

  const handleAddContent = useCallback(
    async (payload: AddLocalizationKeyPayload) => {
      if (payload.contentType === "industries") {
        await localizationApi.createIndustry({
          translations: {
            en: payload.en,
            "zh-CN": payload["zh-CN"],
            ar: payload.ar,
          },
        });
        await queryClient.invalidateQueries({ queryKey: ["localization", "industries"] });
      } else if (payload.contentType === "categories") {
        await localizationApi.createCategory({
          translations: {
            en: payload.en,
            "zh-CN": payload["zh-CN"],
            ar: payload.ar,
          },
        });
        await queryClient.invalidateQueries({ queryKey: ["localization", "categories"] });
      } else {
        await localizationApi.addOtherContent(payload);
        await queryClient.invalidateQueries({ queryKey: ["localization", "other"] });
        await queryClient.invalidateQueries({ queryKey: ["localization", "statistics"] });
      }
    },
    [queryClient]
  );

  return (
    <View className="flex-1 bg-stroke-10">
      <ScrollView
        className="flex-1 bg-white"
        testID="staff-localization"
        contentContainerStyle={{ flexGrow: 1, paddingTop: top }}
      >
        {/* ── Page header ─────────────────────────────────────────── */}
        <View className="px-page-x tablet:px-20 pt-10 mb-6 bg-white items-start">
          <AppText weight="bold" className="text-3xl text-dark-text-50">
            {t("staff.localization.title")}
          </AppText>
          <AppText className="text-sm text-supporting-text mt-1">
            {t("staff.localization.subtitle")}
          </AppText>

          <View className="mt-6 items-start w-full">
            <LocalizationStatCards
              allCount={listData.allCount}
              completedCount={listData.completedCount}
              pendingCount={listData.pendingCount}
            />
          </View>
        </View>

        {/* ── Content (tabs + grid) ────────────────────────────────── */}
        <View className="w-full flex-col mb-10">
          <LocalizationList
            data={listData}
            isLoading={isLoading}
            contentType={contentType}
            onContentTypeChange={handleContentTypeChange}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
            search={search}
            onSearchChange={setSearch}
            sortField={sortField}
            onSortChange={handleSortChange}
            page={page}
            onPageChange={setPage}
            onAddContent={() => setAddModalVisible(true)}
            onCardPress={handleCardPress}
          />
        </View>

        <PageFooter />
      </ScrollView>

      {/* ── Modals ─────────────────────────────────────────────────── */}
      <AddContentModal
        visible={addModalVisible}
        onDismiss={() => setAddModalVisible(false)}
        onSubmit={handleAddContent}
      />
    </View>
  );
}
